// ---------------------------------------------------------------------------
// Design ▸ Components — the screens and reusable components inside the file.
//
// Two catalogues share the screen because the import produces two different
// things: the screen frames the canvas lays out (with their rendered previews)
// and the reusable components behind them (with their parts, options, and the
// colours they paint). Picking a screen here drives the same selection the
// canvas uses, so the two views never disagree.
//
// Preview images are lazy: a card that has not scrolled into view issues no
// request, mirroring the canvas' viewport culling.
// ---------------------------------------------------------------------------

import { Component, Image as ImageIcon, Search } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { OpenPencilFigmaSourceBundle } from "./openpencil-figma-source";
import { useOpenPencilPreviewSource } from "./openpencil-source-canvas";
import {
  buildSourceComponentCatalog,
  filterSourceComponents,
  filterSourceScreens,
  formatScreenSize,
  type SourceComponentRow,
  type SourceScreenRow,
} from "./openpencil-source-library-model";

type CatalogTab = "screens" | "components";

/** Human wording for Figma's component property kinds. */
const PROPERTY_TYPE_LABEL: Record<string, string> = {
  VARIANT: "Option set",
  BOOLEAN: "Show / hide",
  TEXT: "Text",
  INSTANCE_SWAP: "Swappable part",
};

function propertyTypeLabel(type: string): string {
  return PROPERTY_TYPE_LABEL[type] ?? type;
}

/**
 * Reports whether a node has entered the viewport at least once. Without an
 * IntersectionObserver (jsdom, older engines) everything counts as visible,
 * so the fallback is "load", never "silently blank".
 */
function useEnteredViewport<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [entered, setEntered] = useState(
    () => typeof IntersectionObserver === "undefined",
  );
  useEffect(() => {
    if (entered) return;
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setEntered(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setEntered(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [entered]);
  return { ref, entered };
}

function Section({
  title,
  caption,
  action,
  testId,
  children,
}: {
  title: string;
  caption?: ReactNode;
  action?: ReactNode;
  testId: string;
  children: ReactNode;
}) {
  return (
    <section
      className="rounded-lg border border-border bg-card"
      data-testid={testId}
    >
      <header className="flex min-h-12 flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-border px-4 py-2">
        <div className="flex min-w-0 flex-col">
          <h2 className="truncate text-sm font-semibold text-foreground">
            {title}
          </h2>
          {caption ? (
            <p className="text-[11px] leading-4 text-muted-foreground">
              {caption}
            </p>
          ) : null}
        </div>
        {action}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 py-1">
      <dt className="shrink-0 text-[11px] text-muted-foreground">{label}</dt>
      <dd className="min-w-0 flex-1 text-xs text-foreground">{children}</dd>
    </div>
  );
}

function ScreenCard({
  bundle,
  screen,
  selected,
  onSelect,
}: {
  bundle: OpenPencilFigmaSourceBundle;
  screen: SourceScreenRow;
  selected: boolean;
  onSelect: (surfaceId: string) => void;
}) {
  const { ref, entered } = useEnteredViewport<HTMLLIElement>();
  const { source, failed } = useOpenPencilPreviewSource(
    bundle,
    screen.preview ?? undefined,
    entered,
  );
  const [imageBroken, setImageBroken] = useState(false);
  const missingReason = screen.preview
    ? failed || imageBroken
      ? "Preview unavailable"
      : null
    : (screen.previewFailure ?? "No preview rendered");

  return (
    <li ref={ref} data-testid="openpencil-component-screen-card">
      <button
        type="button"
        onClick={() => onSelect(screen.surfaceId)}
        aria-pressed={selected}
        data-testid={`openpencil-component-screen-${screen.surfaceId}`}
        data-selected={selected ? "true" : "false"}
        data-preview={source ? "loaded" : missingReason ? "missing" : "pending"}
        className={`flex w-full flex-col overflow-hidden rounded-md border bg-background text-left transition-colors ${
          selected
            ? "border-primary ring-1 ring-primary"
            : "border-border hover:border-foreground/40"
        }`}
      >
        <span className="flex h-32 w-full items-center justify-center overflow-hidden bg-muted/30">
          {source ? (
            <img
              src={source}
              alt={`${screen.name} preview`}
              loading="lazy"
              draggable={false}
              className="max-h-full max-w-full object-contain"
              onError={() => setImageBroken(true)}
            />
          ) : (
            <span className="flex flex-col items-center gap-1.5 px-2 text-center">
              <ImageIcon
                className="h-5 w-5 text-muted-foreground/60"
                aria-hidden
              />
              {missingReason ? (
                <span className="text-[10px] leading-4 text-muted-foreground">
                  {missingReason}
                </span>
              ) : null}
            </span>
          )}
        </span>
        <span className="flex min-w-0 flex-col gap-0.5 border-t border-border px-2.5 py-2">
          <span
            className="truncate text-xs font-medium text-foreground"
            title={screen.name}
          >
            {screen.name}
          </span>
          <span className="flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
            <span className="tabular-nums">
              {formatScreenSize(screen.width, screen.height)}
            </span>
            <span className="rounded-full border border-border px-1.5">
              {screen.classification === "mobile" ? "phone" : "large screen"}
            </span>
          </span>
          <span
            className="truncate text-[10px] text-muted-foreground"
            title={screen.pageName}
          >
            {screen.pageName}
          </span>
        </span>
      </button>
    </li>
  );
}

function ScreenDetail({ screen }: { screen: SourceScreenRow }) {
  return (
    <dl data-testid="openpencil-component-screen-detail">
      <Detail label="Page">{screen.pageName}</Detail>
      <Detail label="Group">{screen.sectionName ?? "not in a group"}</Detail>
      <Detail label="Size">
        {formatScreenSize(screen.width, screen.height)}
      </Detail>
      <Detail label="Kind">
        {screen.classification === "mobile" ? "Phone-sized" : "Larger than a phone"}
      </Detail>
      <Detail label="Preview">
        {screen.preview
          ? `${screen.preview.renderedWidth}×${screen.preview.renderedHeight} rendered at ${Math.round(screen.preview.scale * 100)}%`
          : (screen.previewFailure ?? "not rendered")}
      </Detail>
      <Detail label="Layers on this screen">
        <span className="text-muted-foreground">
          not extracted
          {screen.pageLayerCount !== null
            ? ` — the file only counts layers per page (${screen.pageLayerCount.toLocaleString()} on ${screen.pageName})`
            : ""}
        </span>
      </Detail>
      <Detail label="Components placed on this page">
        {screen.pageComponentInstanceCount === null ? (
          <span className="text-muted-foreground">
            no component data in this file
          </span>
        ) : (
          `${screen.pageComponentInstanceCount.toLocaleString()} sampled`
        )}
      </Detail>
    </dl>
  );
}

function ComponentDetail({ component }: { component: SourceComponentRow }) {
  const linkedTokens = component.tokenRefs.filter((ref) => ref.declared);
  return (
    <div className="space-y-4" data-testid="openpencil-component-detail">
      <dl>
        <Detail label="Options">
          {component.variantCount.toLocaleString()} combination
          {component.variantCount === 1 ? "" : "s"}
          {component.sampledVariantCount < component.variantCount
            ? ` · ${component.sampledVariantCount.toLocaleString()} read in detail`
            : ""}
          {component.variantCoverageComplete === true
            ? " · every option value covered"
            : component.variantCoverageComplete === false
              ? " · some option values not covered"
              : ""}
        </Detail>
        <Detail label="Layers inspected">
          {component.inspectedNodeCount === null
            ? "not extracted"
            : component.inspectedNodeCount.toLocaleString()}
          {component.partsTruncated ? " (list truncated)" : ""}
        </Detail>
        {component.description ? (
          <Detail label="Note">{component.description}</Detail>
        ) : null}
      </dl>

      <div>
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Settings
        </h4>
        {component.properties.length === 0 ? (
          <p className="mt-1 text-xs text-muted-foreground">
            This component exposes no settings.
          </p>
        ) : (
          <ul className="mt-1.5 space-y-1.5">
            {component.properties.map((property) => {
              const options =
                component.observedVariantValues[property.name] ??
                property.variantOptions ??
                [];
              return (
                <li
                  key={`${property.id}-${property.name}`}
                  className="rounded-md border border-border px-2 py-1.5"
                  data-testid="openpencil-component-property"
                  data-property-type={property.type}
                >
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-xs font-medium text-foreground">
                      {property.name}
                    </span>
                    <span className="rounded-full border border-border px-1.5 text-[10px] text-muted-foreground">
                      {propertyTypeLabel(property.type)}
                    </span>
                  </div>
                  {options.length > 0 ? (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {options.map((option) => (
                        <span
                          key={option}
                          className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
                        >
                          {option}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div>
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Parts
        </h4>
        {component.parts.length === 0 ? (
          <p className="mt-1 text-xs text-muted-foreground">
            No inner parts were read for this component.
          </p>
        ) : (
          <ul
            className="mt-1.5 max-h-56 overflow-auto"
            data-testid="openpencil-component-parts"
          >
            {component.parts.map((part) => (
              <li
                key={`${part.name}-${part.maxDepth}-${part.nodeTypes.join()}`}
                className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-b border-border py-1 last:border-b-0"
              >
                <span className="min-w-0 flex-1 truncate text-xs text-foreground">
                  {part.name}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {part.nodeTypes.join(", ").toLowerCase()}
                </span>
                <span className="text-[10px] tabular-nums text-muted-foreground">
                  seen {part.observedCount}×
                  {part.maxDepth !== null ? ` · depth ${part.maxDepth}` : ""}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Swappable parts
        </h4>
        {component.slots.length === 0 ? (
          <p
            className="mt-1 text-xs text-muted-foreground"
            data-testid="openpencil-component-slots-empty"
          >
            {component.slotStatus === "not-observed"
              ? "No swappable part was observed in this component."
              : "A swap was observed but no target could be read."}
          </p>
        ) : (
          <ul className="mt-1.5 space-y-1">
            {component.slots.map((slot) => (
              <li
                key={slot.propertyName}
                className="flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-md border border-border px-2 py-1.5"
                data-testid="openpencil-component-slot"
              >
                <span className="text-xs text-foreground">
                  {slot.propertyName}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {slot.semanticStatus}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {slot.editableInCogo ? "editable" : "read-only for now"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Colours it paints
        </h4>
        {component.tokenRefs.length === 0 ? (
          <p className="mt-1 text-xs text-muted-foreground">
            No colour of this component matched a variable in the file.
          </p>
        ) : (
          <>
            <ul
              className="mt-1.5 max-h-56 overflow-auto"
              data-testid="openpencil-component-tokens"
            >
              {component.tokenRefs.map((ref) => (
                <li
                  key={ref.variableId}
                  className="flex items-center gap-2 border-b border-border py-1 last:border-b-0"
                >
                  <span
                    className="h-4 w-4 shrink-0 rounded-sm border border-border"
                    style={{ backgroundColor: ref.value }}
                    aria-hidden
                  />
                  <span
                    className="min-w-0 flex-1 truncate text-xs text-foreground"
                    title={ref.variableName}
                  >
                    {ref.variableName}
                  </span>
                  <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
                    {ref.value}
                  </span>
                  <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
                    {ref.usageCount.toLocaleString()}×
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-1.5 text-[10px] leading-4 text-muted-foreground">
              Matched because the component paints that exact colour
              {linkedTokens.length < component.tokenRefs.length
                ? ` · ${linkedTokens.length} of ${component.tokenRefs.length} are variables declared in this file`
                : ""}
              . This is a colour match, not a binding recorded in the file.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

interface Props {
  bundle: OpenPencilFigmaSourceBundle;
  /** Shared with the canvas so both views highlight the same screen. */
  selectedSurfaceId: string;
  onSelectSurface: (surfaceId: string) => void;
}

export function OpenPencilSourceComponents({
  bundle,
  selectedSurfaceId,
  onSelectSurface,
}: Props) {
  const catalog = useMemo(() => buildSourceComponentCatalog(bundle), [bundle]);
  const [tab, setTab] = useState<CatalogTab>("screens");
  const [query, setQuery] = useState("");
  const [selectedComponentId, setSelectedComponentId] = useState("");

  const screens = useMemo(
    () => filterSourceScreens(catalog.screens, query),
    [catalog.screens, query],
  );
  const components = useMemo(
    () => filterSourceComponents(catalog.components, query),
    [catalog.components, query],
  );
  const selectedScreen =
    catalog.screens.find((screen) => screen.surfaceId === selectedSurfaceId) ??
    null;
  const selectedComponent =
    catalog.components.find(
      (component) => component.id === selectedComponentId,
    ) ??
    components[0] ??
    null;

  const selectScreen = useCallback(
    (surfaceId: string) => onSelectSurface(surfaceId),
    [onSelectSurface],
  );

  if (catalog.screens.length === 0 && catalog.components.length === 0) {
    return (
      <div
        className="flex min-h-[260px] items-center justify-center rounded-lg border border-dashed border-border bg-card/40 px-6 py-10 text-center"
        data-testid="openpencil-components-empty"
      >
        <div className="max-w-sm">
          <Component
            className="mx-auto h-8 w-8 text-muted-foreground"
            aria-hidden
          />
          <h2 className="mt-4 text-sm font-semibold text-foreground">
            No screens or components in this design file
          </h2>
          <p className="mt-2 text-xs leading-5 text-muted-foreground">
            Studio found no top-level frames and no reusable components in the
            file you imported. Arrange your screens as frames, publish your
            components, and import the file again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="openpencil-components-view">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border bg-card px-4 py-3">
        <div
          className="inline-flex h-9 overflow-hidden rounded-md border border-border bg-background"
          role="tablist"
          aria-label="Design catalogue"
        >
          {(
            [
              { id: "screens" as const, label: "Screens", count: catalog.screens.length },
              {
                id: "components" as const,
                label: "Components",
                count: catalog.components.length,
              },
            ] satisfies { id: CatalogTab; label: string; count: number }[]
          ).map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={tab === item.id}
              data-testid={`openpencil-components-tab-${item.id}`}
              onClick={() => setTab(item.id)}
              className={`h-9 border-l border-border px-3 text-xs font-medium first:border-l-0 ${
                tab === item.id
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              {item.label} ({item.count.toLocaleString()})
            </button>
          ))}
        </div>
        <div className="flex min-w-0 flex-1 basis-52 items-center gap-2 rounded-md border border-border bg-background px-2">
          <Search
            className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
            aria-hidden
          />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
            placeholder={
              tab === "screens"
                ? "Find a screen by name, page, or size"
                : "Find a component by name or setting"
            }
            aria-label="Find a screen or component"
            data-testid="openpencil-components-search"
            className="h-9 min-w-0 flex-1 bg-transparent text-xs text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
        <span
          className="text-[11px] text-muted-foreground"
          data-testid="openpencil-components-summary"
        >
          {tab === "screens"
            ? `${catalog.previewsRendered}/${catalog.previewsRequested} previews rendered`
            : catalog.declaredComponentSetCount !== null
              ? `${catalog.declaredComponentSetCount.toLocaleString()} in the file · ${(catalog.declaredInstanceCount ?? 0).toLocaleString()} placements`
              : "no component data"}
        </span>
      </div>

      {tab === "screens" ? (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.9fr)_minmax(16rem,1fr)]">
          <Section
            testId="openpencil-components-screens"
            title="Screens"
            caption={
              query
                ? `${screens.length} of ${catalog.screens.length} match “${query}”`
                : `${catalog.screens.length} top-level frames`
            }
          >
            {screens.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No screen matches “{query}”.
              </p>
            ) : (
              <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
                {screens.map((screen) => (
                  <ScreenCard
                    key={screen.surfaceId}
                    bundle={bundle}
                    screen={screen}
                    selected={screen.surfaceId === selectedSurfaceId}
                    onSelect={selectScreen}
                  />
                ))}
              </ul>
            )}
          </Section>
          <Section
            testId="openpencil-components-screen-inspector"
            title={selectedScreen ? selectedScreen.name : "Nothing selected"}
            caption={selectedScreen ? "From your design file" : undefined}
          >
            {selectedScreen ? (
              <ScreenDetail screen={selectedScreen} />
            ) : (
              <p className="text-xs text-muted-foreground">
                Pick a screen to see what the file records about it.
              </p>
            )}
          </Section>
        </div>
      ) : catalog.components.length === 0 ? (
        <div
          className="rounded-lg border border-dashed border-border bg-card/40 px-4 py-8 text-center text-xs leading-5 text-muted-foreground"
          data-testid="openpencil-components-none"
        >
          This design file declares no reusable components. Turn a frame into a
          component in your design tool and import the file again to see it
          here.
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,1.2fr)]">
          <Section
            testId="openpencil-components-list"
            title="Components"
            caption={
              query
                ? `${components.length} of ${catalog.components.length} match “${query}”`
                : `${catalog.components.length} read from the file`
            }
          >
            {components.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No component matches “{query}”.
              </p>
            ) : (
              <ul className="max-h-[520px] overflow-auto">
                {components.map((component) => {
                  const selected = component.id === selectedComponent?.id;
                  return (
                    <li key={component.id}>
                      <button
                        type="button"
                        aria-pressed={selected}
                        data-testid={`openpencil-component-item-${component.id}`}
                        data-selected={selected ? "true" : "false"}
                        onClick={() => setSelectedComponentId(component.id)}
                        className={`flex w-full flex-col items-start gap-0.5 border-b border-border px-2 py-2 text-left last:border-b-0 ${
                          selected ? "bg-muted" : "hover:bg-muted/60"
                        }`}
                      >
                        <span
                          className="w-full truncate text-xs font-medium text-foreground"
                          title={component.name}
                        >
                          {component.name}
                        </span>
                        <span className="flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
                          <span>
                            {component.variantCount.toLocaleString()} option
                            {component.variantCount === 1 ? "" : "s"}
                          </span>
                          <span>·</span>
                          <span>
                            {component.parts.length.toLocaleString()} parts
                          </span>
                          {Object.entries(component.propertyTypeCounts).map(
                            ([type, count]) => (
                              <span
                                key={type}
                                className="rounded-full border border-border px-1.5"
                              >
                                {propertyTypeLabel(type)} {count}
                              </span>
                            ),
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Section>
          <Section
            testId="openpencil-components-inspector"
            title={selectedComponent ? selectedComponent.name : "Nothing selected"}
            caption={
              selectedComponent
                ? selectedComponent.isPublishable
                  ? "Shared component"
                  : "Local to this file"
                : undefined
            }
          >
            {selectedComponent ? (
              <ComponentDetail component={selectedComponent} />
            ) : (
              <p className="text-xs text-muted-foreground">
                Pick a component to see its settings and parts.
              </p>
            )}
          </Section>
        </div>
      )}
    </div>
  );
}
